import { useEffect, useRef, useState } from "react";
import RightModal from "../../../components/Modal/RightModal";
import SavedFilterComponent from "../../../components/SavedFilters/SavedFilterComponent";
import { handleDropdownClose } from "../../../utils/utils";
import BuyerAdvanceFilterData from "./BuyerAdvanceFilterData";

// ✅ Constants
const EMPTY_ADVANCE_FILTER = [];
const ADVANCE_FILTER_KEY = "buyer_advanceFilterData";
const SELECTED_SAVED_FILTER_KEY = "selectedSavefilterId";

const BuyerAdvanceFilter = ({ filterData, onSetFilterData, onCallApiAgain }) => {
  const dropdownRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [selectedSavefilterId, setSelectedSaveFilterId] = useState(
    localStorage.getItem(SELECTED_SAVED_FILTER_KEY) || ""
  );

  // UI empty state for better UX
  const initialEmptyState = [
    {
      id: Date.now(),
      condition: "AND",
      filters: [{ join: null, field: "", operator: "", value: "" }],
    },
  ];

  const [localFilterData, setLocalFilterData] = useState(
    filterData?.length ? filterData : initialEmptyState
  );

  /* ---------------- LOAD FROM LOCALSTORAGE ON MOUNT ---------------- */
  useEffect(() => {
    const savedAdvanceFilter = localStorage.getItem(ADVANCE_FILTER_KEY);
    if (savedAdvanceFilter) {
      try {
        const parsed = JSON.parse(savedAdvanceFilter);
        if (parsed && parsed.length > 0) {
          onSetFilterData(parsed);        // Restore to parent
          onCallApiAgain(parsed);
          setLocalFilterData(parsed);
        }
      } catch (e) {
        console.error("Failed to parse saved advance filter", e);
        localStorage.removeItem(ADVANCE_FILTER_KEY);
      }
    }
  }, []); // Run only once on mount

  /* ---------------- SYNC WHEN SIDEBAR OPENS ---------------- */
  useEffect(() => {
    if (isSidebarOpen) {
      setLocalFilterData(filterData?.length ? filterData : initialEmptyState);
    }
  }, [isSidebarOpen, filterData]);

  /* ---------------- CLOSE ON OUTSIDE CLICK ---------------- */
  useEffect(() => {
    handleDropdownClose(dropdownRef, () => setIsSidebarOpen(false));
  }, []);

  /* ---------------- APPLY FILTER ---------------- */
  const handleFilter = () => {
    onSetFilterData(localFilterData);
    onCallApiAgain(localFilterData);

    // Save ONLY advance filter
    localStorage.setItem(ADVANCE_FILTER_KEY, JSON.stringify(localFilterData));

    setIsSidebarOpen(false);
  };

  /* ---------------- CLEAR ALL (Only Advance Filter) ---------------- */
  const handleClearAll = () => {
    onSetFilterData(EMPTY_ADVANCE_FILTER);
    onCallApiAgain(EMPTY_ADVANCE_FILTER);

    setLocalFilterData(initialEmptyState);
    setSelectedSaveFilterId("");

    localStorage.removeItem(ADVANCE_FILTER_KEY);
    localStorage.removeItem(SELECTED_SAVED_FILTER_KEY);

    setIsSidebarOpen(false);
  };

  /* ---------------- CLEAR FROM SAVED FILTER COMPONENT ---------------- */
  const handleClearFromSavedFilter = () => {
    onSetFilterData(EMPTY_ADVANCE_FILTER);
    onCallApiAgain(EMPTY_ADVANCE_FILTER);

    setLocalFilterData(initialEmptyState);
    setSelectedSaveFilterId("");

    localStorage.removeItem(ADVANCE_FILTER_KEY);
    localStorage.removeItem(SELECTED_SAVED_FILTER_KEY);
  };

  /* ---------------- SELECT SAVED FILTER ---------------- */
  const handleSelectFilter = (value) => {
    const filters = value?.advance_filters || EMPTY_ADVANCE_FILTER;

    onSetFilterData(filters);
    onCallApiAgain(filters);
    setLocalFilterData(filters.length ? filters : initialEmptyState);
    setSelectedSaveFilterId(value?.id || "");

    localStorage.setItem(ADVANCE_FILTER_KEY, JSON.stringify(filters));
    localStorage.setItem(SELECTED_SAVED_FILTER_KEY, value?.id || "");
  };

  /* ---------------- DATA FOR SAVING FILTER ---------------- */
  const saveSearchFilterData = {
    advance_filters: localFilterData,
  };

  /* ---------------- FILTER COUNT ---------------- */
  const filterCount =
    localFilterData?.filter((group) =>
      group?.filters?.some((f) => f.field || f.operator || f.value)
    ).length || 0;

  return (
    <>
      <SavedFilterComponent
        from="buyer"
        filterType="advance"
        count={filterCount}
        isSidebarOpen={isSidebarOpen}
        initialFilterData={EMPTY_ADVANCE_FILTER}
        selectedSavefilterId={selectedSavefilterId}
        saveSearchFilterData={saveSearchFilterData}
        onSetIsSidebarOpen={setIsSidebarOpen}
        onSetFilterData={handleClearFromSavedFilter}
        onCallApiAgain={onCallApiAgain}
        onSetSelectedSaveFilterId={setSelectedSaveFilterId}
        handleSelectFilter={handleSelectFilter}
      />

      <RightModal
        isOpen={isSidebarOpen}
        ref={dropdownRef}
        title="Advance Filter"
        desc="Enter Buyers information."
        from="buyer"
        onFilter={handleFilter}
        onClose={() => setIsSidebarOpen(false)}
        onClearAll={handleClearAll}
        saveSearchFilterData={saveSearchFilterData}
        onSetSelectedSaveFilterId={setSelectedSaveFilterId}
      >
        <BuyerAdvanceFilterData
          isSidebarOpen={isSidebarOpen}
          filterData={localFilterData}
          onSetFilterData={setLocalFilterData}
        />
      </RightModal>
    </>
  );
};

export default BuyerAdvanceFilter;